import {Component, effect, ElementRef, Renderer2} from '@angular/core';
import {MessageService} from '@/_services/message.service';
import {TactaCardComponent} from '@/controls/tacta-card/tacta-card.component';
import {BoardMode, CardConfig, GameMode, Player, PlayerMode, TactaService} from '@/_services/tacta.service';
import {Utils} from '@/classes/utils';
import {TactaBoardComponent} from '@/controls/tacta-board/tacta-board.component';
import {GlobalsService} from '@/_services/globals.service';
import {MatButton, MatIconButton, MatMiniFabButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatFormField, MatInput, MatLabel} from '@angular/material/input';
import {FormsModule} from '@angular/forms';
import {CdkCopyToClipboard} from '@angular/cdk/clipboard';
import {BackendService} from '@/_services/backend.service';
import {TactaCardService} from '@/_services/tacta-card.service';

@Component({
  selector: 'app-site-tacta',
  imports: [
    TactaCardComponent,
    TactaBoardComponent,
    MatIconButton,
    MatIcon,
    MatButton,
    MatFormField,
    MatLabel,
    FormsModule,
    MatInput,
    CdkCopyToClipboard,
    MatMiniFabButton
  ],
  templateUrl: './site-tacta.component.html',
  styleUrl: './site-tacta.component.scss',
})
export class SiteTactaComponent {

  protected readonly Utils = Utils;
  protected editPlayerIdx: number;
  protected readonly BoardMode = BoardMode;
  protected readonly GameMode = GameMode;
  protected readonly PlayerMode = PlayerMode;

  constructor(private renderer: Renderer2,
              private host: ElementRef,
              public msg: MessageService,
              public ts: TactaService,
              public tcs: TactaCardService,
              public bs: BackendService,
              public globals: GlobalsService) {
    effect(() => {
      if (this.ts.boardMode() === BoardMode.Prepare) {
        this.renderer.addClass(this.host.nativeElement, 'prepare');
        this.renderer.removeClass(this.host.nativeElement, 'play');
      } else {
        this.renderer.addClass(this.host.nativeElement, 'play');
        this.renderer.removeClass(this.host.nativeElement, 'prepare');
      }
    });
    this.ts.createDeck();
    while (this.ts.players.length < 2) {
      this.ts.createPlayer();
    }
    this.ts.activateNextPlayer();
  }

  get classForBoard() {
    const ret: string[] = [];
    if (this.ts.board.length > 1) {
      ret.push('multi');
    }
    return ret;
  }

  colorList(player: Player) {
    return this.ts.colors.filter((c) =>
      c.id > 0
      && c.id !== player.color
//      && !this.ts.players.some(p => p.color === c.id)
    );
  }

  classForScore(player: Player) {
    const ret: string[] = [];
    if (player.cards.length === 0) {
      ret.push('final');
      let maxScore = 0;
      for (const p of this.ts.players) {
        maxScore = Math.max(maxScore, p.score);
      }
      if (player.score === maxScore) {
        ret.push('glow-text');
        ret.push('winner');
      } else {
        ret.push('distressed');
      }
    }
    return ret;
  }

  classForScoreQuick(player: Player) {
    const ret: string[] = [];
    ret.push('final');
    let maxScore = 0;
    for (const p of this.ts.players) {
      maxScore = Math.max(maxScore, this.quickScore(p));
    }
    if (this.quickScore(player) === maxScore) {
      ret.push('glow-text');
      ret.push('winner');
    } else {
      ret.push('distressed');
    }
    return ret;
  }

  score(player: Player) {
    const score = Utils.plural(player.score, {1: '1 Punkt', other: `${player.score} Punkte`});
    if (player.cards.length > 0) {
      return `${score} / ${this.cardCount(player)}`;
    }
    return `${score}`;
  }

  quickScore(player: Player) {
    return player.quick.reduce((acc, q) =>
      acc + (q.score >= 0 ? q.score : 0), 0);
  }

  scoreQuick(player: Player) {
    const score = this.quickScore(player);
    return Utils.plural(score, {0: '', 1: '1 Punkt', other: `${score} Punkte`});
  }

  cardCount(player: Player) {
    return Utils.plural(player.cards.length, {1: '1 Karte', other: `${player.cards.length} Karten`});
  }

  styleForColor(player: Player, color: any) {
    const size = (CardConfig.defHeight * 2 / this.colorList(player).length) + 'px';
    return {
      backgroundColor: color.f,
      '--mat-button-text-label-text-color': color.t,
      '--mat-icon-color': color.t,
      height: size,
      width: size
    };
  }

  clickColor(evt: PointerEvent, player: Player, color: any, skipColorPlayer = false) {
    evt.stopPropagation();
    const colorPlayer = this.ts.players.find(p => p.color === color.id);
    const orgColor = this.ts.colors.find(c => c.id === player.color);
    // for (const card of this.ts.board) {
    //   card.changeColor(color.id, player.color);
    // }
    const cards: number[] = [];
    for (const card of player.cards) {
      cards.push(card - player.color * 18 + color.id * 18);
    }
    player.cards = cards;
    player.cvs = [];
    player.color = color.id;
    if (!skipColorPlayer && colorPlayer != null) {
      this.clickColor(evt, colorPlayer, orgColor, true);
    }
    this.ts.refresh.set(!this.ts.refresh());
  }

  clickCycle(evt: PointerEvent, player: Player) {
    evt.stopPropagation();
    const cards = [];
    cards.push(player.cards[player.cards.length - 1]);
    for (let i = 0; i < player.cards.length - 1; i++) {
      cards.push(player.cards[i]);
    }
    player.cards = cards;
    player.cvs = [];
  }

  clickShuffle(evt: PointerEvent, player: Player) {
    evt.stopPropagation();
    Utils.randomize(player.cards);
    player.cvs = [];
  }

  focusPlayerName() {
    setTimeout(() => {
      const input = document.querySelector('input[playerName]') as HTMLInputElement;
      if (input != null) {
        input.focus();
        input.select();
      }
    }, 0);
  }

  clickDeletePlayer(evt: PointerEvent, player: Player) {
    evt.stopPropagation();
    const idx = this.ts.players.findIndex(p => p.color === player.color);
    if (idx >= 0) {
      this.ts.players.splice(idx, 1);
      for (let i = 0; i < this.ts.players.length; i++) {
        this.ts.players[i].playerIdx = i;
      }
      this.ts.initBoard();
    }
  }

  savePlayer(player: Player) {
    player.name = player.name.replace(/[\[\]]/g, '');
    for (const p of this.ts.players) {
      p.cvs = [];
    }
    this.editPlayerIdx = null;
  }

  styleForPlayer(p1: Player): any {
    if (this.ts.boardMode() === BoardMode.Prepare) {
      const p0 = p1.playerIdx === 3 ? p1 : (this.ts.players[p1.playerIdx - 1] ?? p1);
      const p2 = p1.playerIdx === 2 ? p1 : (this.ts.players[p1.playerIdx + 1] ?? p1);
      return {
        background: `linear-gradient(to right, ${this.ts.colors[p0.color].f} -50%, ${this.ts.colors[p1.color].f} 50%, ${this.ts.colors[p2.color].f} 150%)`,
        '--mat-button-text-label-text-color': this.ts.colors[p1.color].t,
        '--mat-form-field-filled-label-text-color': this.ts.colors[p1.color].t,
        '--mat-form-field-filled-focus-label-text-color': this.ts.colors[p1.color].t,
        '--mat-form-field-filled-hover-label-text-color': this.ts.colors[p1.color].t,
        '--mat-form-field-filled-input-text-color': this.ts.colors[p1.color].t,
        '--mat-form-field-outlined-focus-outline-color': this.ts.colors[p1.color].t,
        '--mat-form-field-filled-active-indicator-color': this.ts.colors[p1.color].t,
        '--mat-form-field-filled-hover-active-indicator-color': this.ts.colors[p1.color].t,
        '--mat-form-field-filled-focus-active-indicator-color': this.ts.colors[p1.color].t,
        '--mat-icon-color': this.ts.colors[p1.color].t,
        '--player-color': this.ts.colors[p1.color].f
      }
    }
    return {
      background: `${this.ts.colors[p1.color].f}`,
      '--mat-button-text-label-text-color': this.ts.colors[p1.color].t,
      '--mat-icon-color': this.ts.colors[p1.color].t,
      '--player-color': this.ts.colors[p1.color].f
    }
  }

  clickAddPlayer(evt: PointerEvent) {
    evt.stopPropagation();
    if (this.ts.players.length < this.ts.colors.length - 1) {
      this.ts.createPlayer();
      this.editPlayerIdx = this.ts.players.length - 1;
      this.focusPlayerName();
      this.ts.initBoard();
    }
  }

  clickPlayerName(player: Player) {
    if (player.playerIdx > 0) {
      this.editPlayerIdx = player.playerIdx;
      this.focusPlayerName()
    }
  }

  clickConfirm(evt: PointerEvent) {
    evt.stopPropagation();
    this.ts.removeCardFromPlayers(this.ts.highestTurn().cardIdx);
    this.ts.playerIdx = (this.ts.playerIdx + 1) % this.ts.players.length;
    this.ts.playerMode.set(PlayerMode.SelectCard);
  }

  clickCancel(evt: PointerEvent) {
    evt.stopPropagation();
    const card = this.ts.highestTurn();
    this.ts.removeCard(this.ts.boardCanvas(), card.cardIdx);
    card.parentLink = '';
    card.errorAreas = '';
    card.scale = 2;
    card.cardWidth = card.scale * CardConfig.defWidth;
    card.cardHeight = card.scale * CardConfig.defHeight;
    card.cardBorder = card.scale * CardConfig.defBorder;
    this.ts.playerMode.set(PlayerMode.SelectCard);
    this.ts.clearBoard();
    this.tcs.drawBoard(this.ts.boardCanvas());
  }

  clickSave(evt: PointerEvent) {
    evt.stopPropagation();
    localStorage.setItem('tacta-game', this.ts.boardCanvas().asString());
  }

  clickLoad(evt: PointerEvent) {
    evt.stopPropagation();
    this.tcs.useSaved = !this.tcs.useSaved;
    // this.tcs.loadGame(localStorage.getItem('tacta-game'));
  }

  clickErrorCheck(evt: PointerEvent) {
    evt.stopPropagation();
    this.tcs.errorCheck(this.ts.boardCanvas());
  }
}
